import {
  uid,
  type Repository,
  type Project,
  type Resource,
  type TenantSettings,
} from "@proof/core";
import { api } from "./api";
import type { WorkspaceTransport } from "./workspace-transport";

/** Create actual catalog resources before pinning them as workspace defaults. */
export async function useDocumentDefaults(
  project: Project,
  local: Repository,
  transport?: WorkspaceTransport,
  options: { signal?: AbortSignal; onlyMissing?: boolean } = {},
) {
  const remote = transport?.api || api;
  const page = project.pages[0];
  const resources: Resource[] = [
    {
      id: uid("resource"),
      tenantId: project.tenantId,
      version: 0,
      kind: "page-size",
      name: `${project.name} page`,
      value: { width: page.width, height: page.height, margin: page.margin },
    },
    {
      id: uid("resource"),
      tenantId: project.tenantId,
      version: 0,
      kind: "output",
      name: `${project.name} RGB output`,
      value: { ...project.outputs[0], colorSpace: "rgb" },
    },
  ];
  const settings = await remote<TenantSettings>("/tenant-settings", {
    signal: options.signal,
  });
  const available = options.onlyMissing
    ? await remote<Resource[]>("/resources", { signal: options.signal })
    : [];
  const saved: Resource[] = [];
  for (const resource of resources) {
    const ref = settings.defaultResources[resource.kind];
    if (
      options.onlyMissing &&
      ref &&
      available.some(
        (row) =>
          row.id === ref.id &&
          row.version === ref.version &&
          row.kind === resource.kind &&
          !row.archived &&
          !row.projectId &&
          (row.kind !== "output" ||
            (row.value as { colorSpace?: string }).colorSpace === "rgb"),
      )
    )
      continue;
    const row = await remote<Resource>("/resources", {
      method: "POST",
      signal: options.signal,
      body: JSON.stringify(resource),
    });
    if (!transport || transport.demo)
      await local.saveResource(project.tenantId, { ...row, version: 0 }, 0);
    saved.push(row);
  }
  if (!saved.length) return;
  for (const row of saved)
    settings.defaultResources[row.kind] = { id: row.id, version: row.version };
  await remote("/tenant-settings", {
    method: "PUT",
    signal: options.signal,
    body: JSON.stringify({ settings, expectedRevision: settings.revision }),
  });
  if (transport && !transport.demo) return;
  const browserSettings = await local.tenantSettings(project.tenantId);
  for (const row of saved)
    browserSettings.defaultResources[row.kind] = {
      id: row.id,
      version: row.version,
    };
  await local.saveTenantSettings(
    project.tenantId,
    browserSettings,
    browserSettings.revision,
  );
}
